import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { BaseLayout } from "@layouts/BaseLayout";
import { DashboardLayout } from "@layouts/DashboardLayout";
import { validateCurrentToken } from "@utils/tokenUtils";
import {
  getProfile,
  downloadReceiptPDF,
  connectToTransactionEvents,
  getBookingWaitStatus,
  TransactionEventPayload,
} from "@services/krent.service";
import "./bookingConfirmed.css";
import { toast } from "react-toastify";
import WaitWindowCountdown, {
  WaitWindowCountdownCompact,
  ClockIcon,
  isWaitingForHost,
  TERMINAL_WAIT_STATUSES,
  useWaitWindowCountdown,
} from "./booking-timer";
import "react-responsive-modal/styles.css";
import Modal from "react-responsive-modal";

interface BookingConfirmationProps {
  bookingData?: any;
  paymentReference?: string;
}

type WaitOutcome = "declined" | "timeout" | null;

const BookingConfirmation: React.FC<BookingConfirmationProps> = ({
  bookingData,
  paymentReference,
}) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState<string>(
    bookingData?.status || "paid_pending_confirmation",
  );
  const [sessionExpiresAt, setSessionExpiresAt] = useState<string | undefined>(
    bookingData?.sessionExpiresAt,
  );
  const [hostAcceptedBankTransfer, setHostAcceptedBankTransfer] = useState(
    Boolean(bookingData?.hostAcceptedBankTransfer),
  );
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDeclinedModal, setShowDeclinedModal] = useState(false);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [waitOutcome, setWaitOutcome] = useState<WaitOutcome>(null);

  const isRequestBooking =
    bookingData?.bookingFlow === "request" ||
    (!bookingData?.instantBooking && !!sessionExpiresAt);
  const isManualBankTransfer =
    bookingData?.paymentMethod === "manual_bank_transfer";
  const isPendingBankTransferConfirmation =
    isManualBankTransfer &&
    (transactionStatus === "paid_pending_confirmation" ||
      bookingData?.adminProofVerified === false) &&
    transactionStatus !== "confirmed";
  const isReceiptUnderReview =
    isManualBankTransfer && transactionStatus === "paid_pending_confirmation";
  const isWaiting =
    isRequestBooking &&
    isWaitingForHost(transactionStatus) &&
    !isReceiptUnderReview;
  const isConfirmingPayment = transactionStatus === "pending_payment";
  const isConfirmed = transactionStatus === "confirmed";
  const hostAcceptedAwaitingPayment =
    hostAcceptedBankTransfer &&
    transactionStatus === "paid_pending_confirmation" &&
    !isConfirmed;
  const showHostWaitUi = isWaiting && !hostAcceptedAwaitingPayment;

  const { expired: countdownExpired } = useWaitWindowCountdown(
    isWaiting ? sessionExpiresAt : undefined,
  );

  const handleStatusUpdate = useCallback(
    (
      nextStatus: string,
      nextExpiresAt?: string | null,
      nextHostAccepted?: boolean,
    ) => {
      setTransactionStatus(nextStatus);
      if (nextExpiresAt) {
        setSessionExpiresAt(nextExpiresAt);
      }
      if (nextHostAccepted != null) {
        setHostAcceptedBankTransfer(nextHostAccepted);
      }

      if (nextHostAccepted && nextStatus === "paid_pending_confirmation") {
        setShowTimerModal(false);
      }

      if (nextStatus === "confirmed") {
        setShowTimerModal(false);
        setShowSuccessModal(true);
        return;
      }

      if (nextStatus === "failed_unavailable") {
        setWaitOutcome("declined");
        setShowTimerModal(false);
        setShowDeclinedModal(true);
        return;
      }

      if (nextStatus === "timeout_unconfirmed") {
        setWaitOutcome("timeout");
        setShowTimerModal(false);
        setShowTimeoutModal(true);
      }
    },
    [],
  );

  useEffect(() => {
    if (!bookingData?.status) return;

    setTransactionStatus(bookingData.status);
    if (bookingData.sessionExpiresAt) {
      setSessionExpiresAt(bookingData.sessionExpiresAt);
    }
    setHostAcceptedBankTransfer(Boolean(bookingData.hostAcceptedBankTransfer));

    if (bookingData.status === "confirmed") {
      setShowSuccessModal(true);
      return;
    }

    if (bookingData.status === "failed_unavailable") {
      setWaitOutcome("declined");
      setShowDeclinedModal(true);
      return;
    }

    if (bookingData.status === "timeout_unconfirmed") {
      setWaitOutcome("timeout");
      setShowTimeoutModal(true);
      return;
    }

    if (
      isRequestBooking &&
      isWaitingForHost(bookingData.status) &&
      !isManualBankTransfer &&
      bookingData.sessionExpiresAt
    ) {
      const sessionExpiry = new Date(bookingData.sessionExpiresAt);
      if (sessionExpiry > new Date()) {
        setShowTimerModal(true);
      }
    }
  }, [bookingData, isRequestBooking, isManualBankTransfer]);

  useEffect(() => {
    if (
      !bookingData?.id ||
      !(isWaiting || isConfirmingPayment || isReceiptUnderReview)
    ) {
      return;
    }

    let cancelled = false;
    let eventSource: EventSource | null = null;
    let pollIntervalId: ReturnType<typeof setInterval> | null = null;
    let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempt = 0;

    const cleanupRealtime = () => {
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
      if (pollIntervalId) {
        clearInterval(pollIntervalId);
        pollIntervalId = null;
      }
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId);
        reconnectTimeoutId = null;
      }
    };

    const applyPayload = (payload: TransactionEventPayload) => {
      if (cancelled || !payload.status) return;
      handleStatusUpdate(
        payload.status,
        payload.sessionExpiresAt,
        payload.hostAcceptedBankTransfer,
      );
      if (TERMINAL_WAIT_STATUSES.includes(payload.status)) {
        cleanupRealtime();
      }
    };

    const pollStatus = async () => {
      try {
        const data = await getBookingWaitStatus(bookingData.id);
        if (!cancelled && data?.status) {
          applyPayload(data);
        }
      } catch {
        // polling is a fallback; ignore transient errors
      }
    };

    const connectSse = () => {
      if (cancelled) return;

      eventSource = connectToTransactionEvents(
        bookingData.id,
        applyPayload,
        () => {
          if (cancelled) return;
          eventSource?.close();
          eventSource = null;
          reconnectAttempt += 1;
          const delay = Math.min(1000 * 2 ** reconnectAttempt, 30000);
          reconnectTimeoutId = setTimeout(connectSse, delay);
        },
      );
    };

    connectSse();
    pollIntervalId = setInterval(pollStatus, 8000);
    pollStatus();

    return () => {
      cancelled = true;
      cleanupRealtime();
    };
  }, [
    bookingData?.id,
    isWaiting,
    isConfirmingPayment,
    isReceiptUnderReview,
    handleStatusUpdate,
  ]);

  useEffect(() => {
    if (!countdownExpired || !isWaiting || !bookingData?.id) {
      return;
    }

    let cancelled = false;

    const verifyTimeout = async () => {
      try {
        const data = await getBookingWaitStatus(bookingData.id);
        if (cancelled || !data?.status) return;

        if (data.status === "timeout_unconfirmed") {
          handleStatusUpdate(
            data.status,
            data.sessionExpiresAt,
            data.hostAcceptedBankTransfer,
          );
          return;
        }

        if (isWaitingForHost(data.status)) {
          if (data.hostAcceptedBankTransfer) {
            handleStatusUpdate(
              data.status,
              data.sessionExpiresAt,
              data.hostAcceptedBankTransfer,
            );
          }
          return;
        }

        handleStatusUpdate(
          data.status,
          data.sessionExpiresAt,
          data.hostAcceptedBankTransfer,
        );
      } catch {
        setWaitOutcome("timeout");
        setShowTimeoutModal(true);
      }
    };

    verifyTimeout();
    const intervalId = setInterval(verifyTimeout, 5000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [countdownExpired, isWaiting, bookingData?.id, handleStatusUpdate]);

  const handleDownloadReceipt = async () => {
    if (!paymentReference) return;

    setDownloading(true);
    try {
      const blob = await downloadReceiptPDF(paymentReference);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "krent-receipt.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to download receipt. Please try again.",
      );
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    const abort = new AbortController();
    const auth = {
      isAuthenticated: validateCurrentToken() ? true : false,
    };

    if (auth.isAuthenticated) {
      getProfile(abort.signal)
        .then((re: any) => setUser(re["data"]))
        .catch((_e: any) => {
          // Error handled silently
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }

    return () => abort.abort();
  }, []);

  if (!bookingData) {
    return null;
  }

  const headerTitle = isConfirmed
    ? "Booking confirmed!"
    : isConfirmingPayment
      ? "Confirming your payment"
      : hostAcceptedAwaitingPayment
        ? "Your host accepted"
        : isPendingBankTransferConfirmation
        ? "Receipt uploaded — pending confirmation"
        : isWaiting
          ? "Payment received — waiting for host"
          : waitOutcome === "declined"
            ? "Booking not confirmed"
            : waitOutcome === "timeout"
              ? "Host did not respond in time"
              : "Payment received";

  const headerSubtitle = isConfirmed
    ? "Your reservation has been successfully confirmed"
    : isConfirmingPayment
      ? "This usually takes a few seconds. We'll update this page as soon as payment is confirmed."
    : hostAcceptedAwaitingPayment
      ? "We're confirming your payment. This page will update when that's done."
    : isPendingBankTransferConfirmation
        ? "We've received your transfer receipt. Your booking will be confirmed once payment is verified."
        : isWaiting
          ? "Your host has a limited time to accept. We'll update this page as soon as they respond."
          : waitOutcome === "declined"
            ? "Your host couldn't confirm these dates. You won't be charged."
            : waitOutcome === "timeout"
              ? "Our team will process your refund and reach out to you shortly."
              : "Your payment was received successfully";

  const bookingContent = (
    <>
      <div
        className={`booking-wrapper ${
          isWaiting || isPendingBankTransferConfirmation || isConfirmingPayment
            ? "booking-wrapper--waiting"
            : ""
        }`}
      >
        <div className="container">
          <div className="text-center mb-4">
            <div
              className={`check-circle ${
                isWaiting ||
                isPendingBankTransferConfirmation ||
                isConfirmingPayment
                  ? "check-circle--waiting"
                  : ""
              }`}
            >
              {isWaiting ||
              isPendingBankTransferConfirmation ||
              isConfirmingPayment ? (
                <ClockIcon />
              ) : (
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            <h1 className="booking-title">{headerTitle}</h1>
            <p className="booking-subtitle">{headerSubtitle}</p>
            <p className="booking-ref">
              Booking reference:{" "}
              <strong style={{ textTransform: "uppercase" }}>
                {paymentReference || bookingData?.referenceId || ""}
              </strong>
            </p>
          </div>

          {isPendingBankTransferConfirmation && (
            <div className="wait-window-inline-card">
              <div className="wait-window-inline-card__header">
                <ClockIcon />
                <div>
                  <strong>Pending payment verification</strong>
                  <p>
                    Your receipt has been uploaded. Krent will review and
                    confirm your transfer shortly.
                  </p>
                </div>
              </div>
            </div>
          )}

          {showHostWaitUi && (
            <div className="wait-window-inline-card">
              <div className="wait-window-inline-card__header">
                <ClockIcon />
                <div>
                  <strong>Host response window</strong>
                  <p>Time remaining for your host to accept this booking.</p>
                </div>
                <WaitWindowCountdownCompact
                  sessionExpiresAt={sessionExpiresAt}
                />
              </div>
              <button
                type="button"
                className="wait-window-inline-card__button"
                onClick={() => setShowTimerModal(true)}
              >
                View details
              </button>
            </div>
          )}

          <div className="booking-card">
            <div className="d-flex gap-4 mb-3">
              {bookingData?.propertyImage ? (
                <img
                  src={bookingData?.propertyImage}
                  alt={bookingData?.propertyName || "Property"}
                  className="property-img-placeholder"
                  style={{
                    width: "140px",
                    height: "140px",
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                />
              ) : (
                <div
                  className="property-img-placeholder"
                  style={{
                    width: "80px",
                    height: "80px",
                    backgroundColor: "#e5e7eb",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  🏡
                </div>
              )}
              <div style={{ flex: 1 }}>
                <div className="property-name">
                  {bookingData?.propertyName || "Property Name"}
                </div>
                <div className="detail-row">
                  <i className="fa-regular fa-location-dot"></i>
                  <span> Lekki, Lagos</span>
                </div>

                <div className="detail-row">
                  <div className="detail-icon">
                    <i className="fa-regular fa-calendar"></i>
                  </div>
                  <div>
                    <div className="detail-label">Check-in</div>
                    <div className="detail-value">
                      {bookingData?.dateFrom
                        ? bookingData?.dateFrom.toLocaleDateString()
                        : "Date"}{" "}
                      · After 3:00 PM
                    </div>
                  </div>
                </div>

                <div className="detail-row">
                  <div className="detail-icon">
                    <i className="fa-regular fa-calendar"></i>
                  </div>
                  <div>
                    <div className="detail-label">Check-out</div>
                    <div className="detail-value">
                      {bookingData?.dateTo
                        ? bookingData?.dateTo.toLocaleDateString()
                        : "Date"}{" "}
                      · Before 11:00 AM
                    </div>
                  </div>
                </div>

                <div className="detail-row">
                  <div className="detail-icon">
                    <i className="fa-regular fa-users "></i>
                  </div>
                  <div>
                    <div className="detail-label">Guests</div>
                    <div className="detail-value">
                      {bookingData?.guests
                        ? `${bookingData?.guests?.adults + (bookingData?.guests?.children || 0) + (bookingData?.guests?.infants || 0)} guests`
                        : "1 guest"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="payment-label fw-semibold">Payment summary</div>
            <div className="payment-row">
              <span>
                {bookingData?.currency || "₦"}
                {bookingData?.pricePerNight?.toLocaleString() || "0"} x{" "}
                {bookingData?.numberOfDays || 1} days
              </span>
              <span>
                {bookingData?.currency || "₦"}
                {(
                  (bookingData?.pricePerNight || 0) *
                  (bookingData?.numberOfDays || 1)
                ).toLocaleString()}
              </span>
            </div>
            <div className="payment-row">
              <span>Caution fee</span>
              <span>
                {bookingData?.currency || "₦"}
                {bookingData?.cautionFee?.toLocaleString() || "0"}
              </span>
            </div>
            <div className="payment-row">
              <span>Service fee</span>
              <span>
                {bookingData?.currency || "₦"}
                {bookingData?.serviceFee?.toLocaleString() || "0"}
              </span>
            </div>
            <div className="payment-total">
              <span className="fw-semibold">
                {isPendingBankTransferConfirmation
                  ? "Total to pay"
                  : "Total paid"}
              </span>
              <span className="fw-semibold">
                {bookingData?.currency || "₦"}
                {(
                  (bookingData?.pricePerNight || 0) *
                    (bookingData?.numberOfDays || 1) +
                  (bookingData?.cautionFee || 0) +
                  (bookingData?.serviceFee || 0)
                ).toLocaleString()}
              </span>
            </div>

            <div className="d-flex gap-2 justify-content-end flex-wrap mt-3">
              {!isPendingBankTransferConfirmation && (
                <button
                  className="bg-white p-1 px-2 rounded-2 gap-3"
                  style={{ border: "1px solid #e5e5e5" }}
                  onClick={handleDownloadReceipt}
                  disabled={downloading || !paymentReference}
                >
                  <i className="fa-regular fa-download"></i>
                  <span className="fs-12">
                    {downloading
                      ? " Downloading..."
                      : " Download confirmation"}
                  </span>
                </button>
              )}

              <button
                className="bg-white p-1 px-2 rounded-2 gap-3"
                style={{ border: "1px solid #e5e5e5" }}
              >
                <i className="fa-regular fa-share-nodes"></i>
                <span className="fs-12"> Share booking</span>
              </button>

              {isPendingBankTransferConfirmation && (
                <div className="d-flex align-items-center gap-2 text-warning fs-10">
                  <i className="fa-solid fa-clock"></i>
                  <span>Pending confirmation</span>
                </div>
              )}

              {isConfirmed && (
                <div className="d-flex align-items-center gap-2 text-success fs-10">
                  <i className="fa-solid fa-check-circle"></i>
                  <span>Booking Confirmed</span>
                </div>
              )}

              {waitOutcome === "declined" && (
                <div className="d-flex align-items-center gap-2 text-danger fs-10">
                  <i className="fa-solid fa-times-circle"></i>
                  <span>Host declined this booking</span>
                </div>
              )}

              {waitOutcome === "timeout" && (
                <div className="d-flex align-items-center gap-2 text-danger fs-10">
                  <i className="fa-solid fa-clock"></i>
                  <span>Wait window expired</span>
                </div>
              )}
            </div>
          </div>

          <div className="next-steps-card">
            <div className="next-steps-title fw-semibold mb-3">Next steps</div>

            {hostAcceptedAwaitingPayment ? (
              <>
                <div className="step-row">
                  <div className="step-number">1</div>
                  <div>
                    <div className="step-title">Your host accepted</div>
                    <div className="step-desc">
                      We&apos;re confirming your payment. Keep this page open or
                      check back shortly.
                    </div>
                  </div>
                </div>
              </>
            ) : isWaiting ? (
              <>
                <div className="step-row">
                  <div className="step-number">1</div>
                  <div>
                    <div className="step-title">Wait for your host</div>
                    <div className="step-desc">
                      Keep this page open or check back later. We&apos;ll notify
                      you as soon as your host accepts or declines.
                    </div>
                  </div>
                </div>
                <div className="step-row">
                  <div className="step-number">2</div>
                  <div>
                    <div className="step-title">Check your email</div>
                    <div className="step-desc">
                      We&apos;ve sent your payment receipt and booking details
                      to your email.
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="step-row">
                  <div className="step-number">1</div>
                  <div>
                    <div className="step-title">Check your email</div>
                    <div className="step-desc">
                      We&apos;ve sent your confirmation and booking details to
                      your email address.
                    </div>
                  </div>
                </div>

                {!bookingData?.renter?.kycVerified && (
                  <div className="step-row">
                    <div className="step-number">2</div>
                    <div>
                      <div className="step-title">
                        Complete identity verification (KYC)
                      </div>
                      <div className="step-desc">
                        Before check-in, you&apos;ll need to verify your
                        identity. This takes under 2 minutes.
                      </div>
                      <a href="/settings?tab=kyc" className="step-link">
                        Complete verification now →
                      </a>
                    </div>
                  </div>
                )}

                <div className="step-row" style={{ marginBottom: 0 }}>
                  <div className="step-number">
                    {bookingData?.renter?.kycVerified ? 2 : 3}
                  </div>
                  <div>
                    <div className="step-title">Contact your host</div>
                    <div className="step-desc">
                      Feel free to message{" "}
                      {bookingData?.owner?.firstname || "your host"} for any
                      questions about your stay.
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="d-flex gap-3 mt-4">
            <button
              className="btn-primary w-full rounded-4"
              onClick={() => navigate("/")}
            >
              Back to home
            </button>
            <button
              className="btn-secondary-action rounded-4"
              onClick={() => navigate("/my-bookings")}
            >
              View my bookings
            </button>
          </div>
        </div>
      </div>

      {showHostWaitUi && (
        <div className="wait-window-banner" role="status" aria-live="polite">
          <div className="wait-window-banner__content">
            <div className="wait-window-banner__icon">
              <ClockIcon />
            </div>
            <div className="wait-window-banner__text">
              <strong>Waiting for your host</strong>
              <span>We&apos;ll update this page when they respond.</span>
            </div>
            <WaitWindowCountdownCompact
              sessionExpiresAt={sessionExpiresAt}
            />
            <button
              type="button"
              className="wait-window-banner__action"
              onClick={() => setShowTimerModal(true)}
            >
              Details
            </button>
          </div>
        </div>
      )}
    </>
  );

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {user && user?.id ? (
        <DashboardLayout>{bookingContent}</DashboardLayout>
      ) : (
        <BaseLayout>{bookingContent}</BaseLayout>
      )}

      <Modal
        open={showTimerModal && showHostWaitUi}
        onClose={() => setShowTimerModal(false)}
        center
        closeOnOverlayClick={false}
        showCloseIcon={true}
        styles={{
          modal: {
            borderRadius: "16px",
            padding: "0",
            maxWidth: "500px",
            width: "90%",
          },
          overlay: {
            background: "rgba(255, 255, 255, 0.7)",
          },
        }}
      >
        <WaitWindowCountdown sessionExpiresAt={sessionExpiresAt} />
      </Modal>

      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        center
        closeOnOverlayClick={false}
        showCloseIcon={true}
        styles={{
          modal: {
            borderRadius: "16px",
            padding: "40px",
            maxWidth: "500px",
            width: "90%",
            textAlign: "center",
          },
          overlay: {
            background: "rgba(255, 255, 255, 0.7)",
          },
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <svg
            width="81"
            height="81"
            viewBox="0 0 81 81"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="0.5"
              y="0.5"
              width="80"
              height="80"
              rx="40"
              fill="#0F5132"
              fillOpacity="0.1"
            />
            <rect
              x="0.5"
              y="0.5"
              width="80"
              height="80"
              rx="40"
              stroke="white"
              strokeOpacity="0.4"
            />
            <path
              d="M35.1861 56.9389L32.212 51.9264L26.5645 50.6889L27.1157 44.8751L23.2891 40.4968L27.1157 36.1184L26.5645 30.3047L32.212 29.0672L35.1861 24.0547L40.5003 26.3109L45.8145 24.0547L48.7886 29.0672L54.4361 30.3047L53.8849 36.1184L57.7116 40.4968L53.8849 44.8751L54.4361 50.6889L48.7886 51.9264L45.8145 56.9389L40.5003 54.6826L35.1861 56.9389ZM36.2503 53.7468L40.5003 51.9455L44.8016 53.7468L47.167 49.7468L51.7503 48.6955L51.3336 43.9968L54.417 40.4968L51.3336 36.9455L51.7503 32.2468L47.167 31.2468L44.7503 27.2468L40.5003 29.048L36.1991 27.2468L33.8336 31.2468L29.2503 32.2468L29.667 36.9455L26.5836 40.4968L29.667 43.9968L29.2503 48.7468L33.8336 49.7468L36.2503 53.7468ZM38.7503 45.8364L47.5899 36.9968L45.8336 35.1893L38.7503 42.2726L35.167 38.7405L33.4107 40.4968L38.7503 45.8364Z"
              fill="#0F5132"
            />
          </svg>
        </div>
        <h2
          style={{
            fontSize: "22px",
            fontWeight: "700",
            color: "#111827",
            marginBottom: "12px",
          }}
        >
          You&apos;re all set
        </h2>
        <p
          style={{
            fontSize: "14px",
            color: "#6B7280",
            marginBottom: "24px",
          }}
        >
          Your host confirmed your stay. View your bookings for check-in
          details.
        </p>
        <button
          onClick={() => {
            setShowSuccessModal(false);
            navigate("/my-bookings");
          }}
          style={{
            backgroundColor: "#47BBA9",
            color: "#fff",
            border: "none",
            borderRadius: "12px",
            padding: "14px 32px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: "pointer",
          }}
        >
          View My Bookings
        </button>
      </Modal>

      <Modal
        open={showDeclinedModal}
        onClose={() => setShowDeclinedModal(false)}
        center
        closeOnOverlayClick={false}
        showCloseIcon={true}
        styles={{
          modal: {
            borderRadius: "16px",
            padding: "40px",
            maxWidth: "500px",
            width: "90%",
            textAlign: "center",
          },
          overlay: {
            background: "rgba(255, 255, 255, 0.7)",
          },
        }}
      >
        <h2
          style={{
            fontSize: "22px",
            fontWeight: "700",
            color: "#111827",
            marginBottom: "12px",
          }}
        >
          Host couldn&apos;t confirm
        </h2>
        <p
          style={{
            fontSize: "14px",
            color: "#6B7280",
            marginBottom: "24px",
          }}
        >
          Your host declined this booking. You won&apos;t be charged. Try
          different dates or browse other properties.
        </p>
        <button
          onClick={() => {
            setShowDeclinedModal(false);
            navigate("/");
          }}
          style={{
            backgroundColor: "#47BBA9",
            color: "#fff",
            border: "none",
            borderRadius: "12px",
            padding: "14px 32px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: "pointer",
          }}
        >
          Browse Properties
        </button>
      </Modal>

      <Modal
        open={showTimeoutModal}
        onClose={() => setShowTimeoutModal(false)}
        center
        closeOnOverlayClick={false}
        showCloseIcon={true}
        styles={{
          modal: {
            borderRadius: "16px",
            padding: "40px",
            maxWidth: "500px",
            width: "90%",
            textAlign: "center",
          },
          overlay: {
            background: "rgba(255, 255, 255, 0.7)",
          },
        }}
      >
        <h2
          style={{
            fontSize: "22px",
            fontWeight: "700",
            color: "#111827",
            marginBottom: "12px",
          }}
        >
          Host didn&apos;t respond in time
        </h2>
        <p
          style={{
            fontSize: "14px",
            color: "#6B7280",
            marginBottom: "24px",
          }}
        >
          The wait window ended before your host responded. Our team will process
          your refund and contact you with next steps.
        </p>
        <button
          onClick={() => {
            setShowTimeoutModal(false);
            navigate("/my-bookings");
          }}
          style={{
            backgroundColor: "#47BBA9",
            color: "#fff",
            border: "none",
            borderRadius: "12px",
            padding: "14px 32px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: "pointer",
          }}
        >
          View My Bookings
        </button>
      </Modal>
    </>
  );
};

export default BookingConfirmation;
